// ── Color Tokens ──────────────────────────────────────────────
export const colors = {
  // Primary card scheme
  cardBackground: '#f8fafc',
  cardHeaderBackground: '#1e293b',
  cardHeaderText: '#ffffff',
  cardBodyText: '#374151',

  // Alert / status scheme
  urgent: '#dc2626',
  urgentBackground: '#fef2f2',
  // warning: accessible amber — 3.19:1 contrast on #ffffff (meets WCAG 3:1 for non-text)
  warning: '#d97706',
  warningBackground: '#fffbeb',
  success: '#059669',
  successBackground: '#f0fdf4',
  info: '#3b82f6',
  infoBackground: '#eff6ff',

  // Neutral palette
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  // textMuted: accessible muted text color — 4.83:1 contrast on #ffffff (WCAG AA)
  textMuted: '#6b7280',
  surface: '#ffffff',
  surfaceAlt: '#f9fafb',
  divider: '#e5e7eb',
  pageBackground: 'transparent', // page uses existing gradient

  // ── Accessibility-specific tokens ───────────────────────────
  // Completed stage text — must meet 4.5:1 even without opacity tricks
  // #4b5563 on #ffffff = 7.56:1 contrast (WCAG AAA)
  completedStageText: '#4b5563',
  // Guide rail completed segment — non-text, needs 3:1 on #ffffff
  // #7c8490 on #ffffff = 3.78:1 contrast (WCAG AA non-text)
  guideRailCompleted: '#7c8490',
  // Guide rail upcoming segment — non-text, needs 3:1 on #ffffff
  // #8b939f on #ffffff = 3.10:1 contrast (WCAG AA non-text)
  guideRailUpcoming: '#8b939f',
  // Focus indicator — 2px outline, needs 3:1 against adjacent colors
  // #2563eb on #ffffff = 5.17:1 contrast (exceeds 3:1 requirement)
  focusIndicator: '#2563eb',
  // Info color for text usage — needs 4.5:1 for WCAG AA text
  // #2563eb on #ffffff = 5.17:1, on #eff6ff = 4.75:1 (both pass)
  infoText: '#2563eb',
};

// ── Spacing Tokens ────────────────────────────────────────────
export const spacing = {
  cardPadding: '16px',
  cardPaddingMobile: '12px',
  cardMargin: '12px 0',
  cardGap: '12px',
  headerPadding: '14px 16px',
  sectionGap: '16px',
  elementGap: '8px',
};

// ── Border Radius Tokens ──────────────────────────────────────
export const radii = {
  card: '12px',
  inner: '8px',
  badge: '10px',
  pill: '999px',
  button: '8px',
};

// ── Typography Tokens ─────────────────────────────────────────
export const typography = {
  heading: { fontSize: '20px', fontWeight: 700 },
  subheading: { fontSize: '16px', fontWeight: 600 },
  body: { fontSize: '14px', fontWeight: 400 },
  caption: { fontSize: '12px', fontWeight: 400 },
  badge: { fontSize: '11px', fontWeight: 500 },
  small: { fontSize: '10px', fontWeight: 400 },
};

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ── Shadow Tokens ─────────────────────────────────────────────
export const shadows = {
  card: '0 1px 3px 0 rgba(0, 0, 0, 0.06)',
  cardHover: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  none: 'none',
};

// ── Reusable Style Objects ────────────────────────────────────
export const cardStyles = {
  container: {
    backgroundColor: colors.cardBackground,
    borderRadius: radii.card,
    border: 'none',
    overflow: 'hidden',
    boxShadow: shadows.card,
  },
  header: {
    padding: spacing.headerPadding,
    backgroundColor: colors.cardHeaderBackground,
    color: colors.cardHeaderText,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.elementGap,
  },
  headerText: {
    fontSize: typography.body.fontSize,
    fontWeight: fontWeights.semibold,
  },
  body: {
    padding: spacing.cardPadding,
  },
};

export const alertCardStyles = {
  urgent: {
    container: {
      ...cardStyles.container,
      backgroundColor: colors.urgentBackground,
    },
    header: {
      ...cardStyles.header,
      backgroundColor: colors.urgent,
    },
  },
  warning: {
    container: {
      ...cardStyles.container,
      backgroundColor: colors.warningBackground,
    },
    header: {
      ...cardStyles.header,
      backgroundColor: colors.warning,
    },
  },
  success: {
    container: {
      ...cardStyles.container,
      backgroundColor: colors.successBackground,
    },
    header: {
      ...cardStyles.header,
      backgroundColor: colors.success,
    },
  },
  info: {
    container: {
      ...cardStyles.container,
      backgroundColor: colors.infoBackground,
    },
    header: {
      ...cardStyles.header,
      backgroundColor: colors.info,
    },
  },
};

export const badgeStyle = {
  fontSize: typography.badge.fontSize,
  fontWeight: fontWeights.medium,
  padding: '2px 8px',
  borderRadius: radii.badge,
};

export const pillButtonStyle = {
  borderRadius: radii.pill,
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.medium,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'all 0.15s',
};

export const actionButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: radii.inner,
  fontSize: '12px',
  fontWeight: fontWeights.medium,
  border: '1px solid',
  cursor: 'pointer',
  transition: 'all 0.15s',
  boxShadow: 'none',
};

// ── Responsive Helpers ────────────────────────────────────────
export const getResponsiveCardStyles = (isMobile) => ({
  container: {
    ...cardStyles.container,
    ...(isMobile ? { padding: spacing.cardPaddingMobile } : {}),
  },
  body: {
    padding: isMobile ? spacing.cardPaddingMobile : spacing.cardPadding,
  },
});

export const getResponsiveMainContent = (isMobile) => ({
  paddingLeft: isMobile ? '0' : '0',
  marginLeft: isMobile ? '0' : '250px',
  width: isMobile ? '100%' : 'calc(100% - 250px)',
});

export const getResponsiveFlightGrid = (isMobile) => ({
  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr',
});

export const getResponsiveActionButtons = (isMobile) => ({
  flexWrap: isMobile ? 'wrap' : 'nowrap',
  gap: isMobile ? '6px' : '8px',
});
